import { Navigate, useNavigate } from "react-router";
import { useStore } from "_core/store";
import {
  IndraChargerCustomisation as IndraChargerCustomisationValidator,
  FormValues,
} from "_core/validators/ChargerCustomisation/Indra";
import { CableSetup, ProductUrls } from "_core/types";
import Mixpanel from "_core/analytics/Mixpanel";
import {
  AnalyticsEvents,
  DataLayerEvent,
} from "_core/analytics/AnalyticsEvents";
import { DataLayer } from "_core/analytics/DataLayer";
import { FormApi, Formy } from "_core/form";
import {
  Button,
  Li,
  Ol,
  P,
  Stack,
  Strong,
  Ul,
  Accordion,
  AccordionGroup,
  RadioCard,
  RadioField,
  Heading4,
} from "@ovotech/element";
import { PhoneContact } from "_core/components/PhoneContact";
import { EmailContact } from "_core/components/EmailContact";
import {
  PRICE_INDRA_TETHERED,
  PRICE_INDRA_UNTETHERED,
} from "_core/config/prices";
import Tethered from "_assets/tethered.png";
import Untethered from "_assets/untethered.png";
import * as React from "react";
import { SplitHeading } from "_core/components";

export const IndraChargerCustomisation = () => {
  const navigate = useNavigate();
  const { cableSetup, setCableSetup, offStreetParking, charger } = useStore();
  const handleSubmit = async (values: FormValues) => {
    setCableSetup(values.cableSetup);
    navigate(ProductUrls.PersonalDetails);

    Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
      buttonId: "cableSetup-submit-button",
      pageName: ProductUrls.ChargerCustomisation,
      cableSetup,
      charger,
    });

    DataLayer.push({
      event: DataLayerEvent.SubmitCableSetup,
      pageName: ProductUrls.ChargerCustomisation,
      cableSetup,
      charger,
    });
  };

  if (!offStreetParking) return <Navigate to={ProductUrls.Postcode} />;

  const priceFormatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    maximumFractionDigits: 0,
    currency: "GBP",
  });

  return (
    <Formy
      initialValues={{
        cableSetup: cableSetup ? cableSetup : "",
      }}
      validationSchema={IndraChargerCustomisationValidator}
      onSubmit={handleSubmit}
    >
      {({
        handleSubmit,
        values,
        errors,
        setFieldValue,
      }: FormApi<FormValues>) => {
        return (
          <form data-testid="cableSetup-form" onSubmit={handleSubmit}>
            <Stack spaceBetween={10}>
              <Stack spaceBetween={8}>
                <SplitHeading part1="Now choose" part2="your cable setup" />
                <P size={"sm"} className={"SurfaceVariant"}>
                  Need help choosing? Call us on <PhoneContact />, or email us
                  at <EmailContact />.
                </P>
              </Stack>
              <div>
                <RadioField
                  className={"RadioField"}
                  id="cableSetup"
                  name="cableSetup"
                  label={
                    <Heading4 className={"SurfaceHeading"}>
                      Cable setup
                    </Heading4>
                  }
                  error={errors.cableSetup}
                >
                  <RadioCard
                    className={"RadioCard"}
                    name="cableSetup"
                    value="tethered"
                    id="cableSetup-tethered"
                    label={
                      <div>
                        <img
                          width={72}
                          alt={"image of a tethered charger"}
                          src={Tethered}
                        ></img>
                        <P>
                          <Strong>Tethered</Strong> from{" "}
                          <Strong>
                            {priceFormatter.format(PRICE_INDRA_TETHERED)}¹
                          </Strong>
                          <br />
                          This includes the charging cable.
                          <br />
                          (Type 2 plug)
                        </P>
                      </div>
                    }
                    checked={values.cableSetup === CableSetup.TETHERED}
                    onClick={() => {
                      setFieldValue("cableSetup", CableSetup.TETHERED);
                      Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
                        buttonId: `cableSetup-${CableSetup.TETHERED}`,
                        pageName: ProductUrls.ChargerCustomisation,
                        charger,
                        value: CableSetup.TETHERED,
                      });
                    }}
                  />
                  <RadioCard
                    className={"RadioCard"}
                    name="cableSetup"
                    value="untethered"
                    id="cableSetup-untethered"
                    label={
                      <div>
                        <img
                          width={72}
                          alt={"image of a untethered charger"}
                          src={Untethered}
                        ></img>
                        <P>
                          <Strong>Untethered</Strong> from{" "}
                          <Strong>
                            {priceFormatter.format(PRICE_INDRA_UNTETHERED)}¹
                          </Strong>
                          <br />
                          This doesn’t include the charging cable.
                          <br />
                          (Type 2 plug)
                        </P>
                      </div>
                    }
                    checked={values.cableSetup === CableSetup.UNTETHERED}
                    onClick={() => {
                      setFieldValue("cableSetup", CableSetup.UNTETHERED);
                      Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
                        buttonId: `cableSetup-${CableSetup.UNTETHERED}`,
                        pageName: ProductUrls.ChargerCustomisation,
                        charger,
                        value: CableSetup.UNTETHERED,
                      });
                    }}
                  />
                </RadioField>
                <P size={"sm"} className={"SurfaceVariant"}>
                  ¹ Including standard installation. The final costs will be
                  displayed in your quote after the online site survey has been
                  submitted and assessed.
                </P>
              </div>
              <div className={"ButtonContainer"}>
                <Button
                  variant="outline"
                  iconLeft="chevron-left"
                  data-testid="cableSetup-back-button"
                  onClick={() => navigate(ProductUrls.OffStreetParking)}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  iconRight="chevron-right"
                  data-testid="cableSetup-submit-button"
                >
                  Next
                </Button>
              </div>
              <AccordionGroup>
                <Accordion title="What’s included in a standard installation?">
                  <P>
                    Our standard charger installations take around 3 hours to
                    complete and include:
                  </P>
                  <Ul>
                    <Li>
                      Our expert engineers fitting the EV charger to a
                      brick/rendered wall.
                    </Li>

                    <Li>
                      Up to 10 meters of 6mm power cable – this is neatly
                      clipped to the wall between the electricity supply meter
                      and the charger.
                    </Li>

                    <Li>
                      Setting up the charger and pairing with your smartphone
                      via WiFi or 4G – with full support from our engineer.
                    </Li>

                    <Li>A full demonstration of how your new charger works.</Li>
                  </Ul>
                  <P>For a standard installation to go ahead:</P>
                  <Ol>
                    <Li>
                      The existing electrical connections and protections within
                      your home must be in good condition.
                    </Li>

                    <Li>
                      All required main protective bonding at the property must
                      be installed and adequately sized before the installation
                      of the Charge point.
                    </Li>

                    <Li>
                      No groundworks/civil works, electrical remedial works or
                      any other trades need to be carried out.
                    </Li>
                  </Ol>

                  <P>
                    If any of these requirements aren’t met, our surveyors will
                    let you know. But please note that any work that needs to be
                    done would need to be arranged by the customer. Our
                    engineers can carry out Earth bonding upgrades for an
                    additional cost. Main isolation switches can also be fitted
                    for OVO customers on the day of install for an additional
                    cost.
                  </P>
                </Accordion>
                <Accordion title="What’s the difference between tethered and untethered cable?">
                  Tethered charger: includes a permanently attached cable, so
                  you can simply park up and plug in. Untethered charger: no
                  cable included. Some EVs come ready with a cable, or you might
                  have to buy your own. Either way, you can keep it in your car,
                  and attach it when you&apos;re ready to charge.
                </Accordion>
              </AccordionGroup>
            </Stack>
          </form>
        );
      }}
    </Formy>
  );
};
